import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { TRANSITIONS, type Priority } from '@lixionary-jira/domain';
import {
  BookOpen,
  Download,
  Eye,
  EyeOff,
  Hammer,
  PackageCheck,
  Paperclip,
  Rocket,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError, api } from '../../api/client';
import { useAction } from '../../api/hooks';
import { useIsAdmin, useMe } from '../../api/session';
import type { TicketDetail } from '../../api/ticket-types';
import type { AuditEvent, Page } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge, StatusBadge, humanize } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, QueryView } from '../../ui/feedback';
import { Checkbox, Field, Input, ListEditor, Segmented, Select, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ActorName, Avatar, Card, KeyValue, LoadMore, Tabs, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { EventTable } from '../shared/EventTable';
import { TicketActionDialog, type DialogAction } from './ActionDialog';
import { AssistantPanel } from './AssistantPanel';
import { PRIORITY_OPTIONS, PriorityBadge, TicketFlags, TypeLabel, formatBytes } from './bits';

type Tab = 'overview' | 'discussion' | 'implementation' | 'approvals' | 'delivery' | 'history';

/** TKT-02: full scope, state, relationships, discussion, approvals, implementation and delivery evidence. */
export function TicketDetailPage() {
  const { key, number } = useParams();
  const n = Number(number);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'overview';
  const q = useQuery({
    queryKey: ['ticket', key, n],
    queryFn: () => api.get<TicketDetail>(`/projects/${key}/tickets/${n}`),
  });
  usePageTitle(q.data ? `${q.data.key} · ${q.data.title}` : null);
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Board', to: `/projects/${key}/board` },
    { label: q.data?.key ?? `${key}-${n}` },
  ]);
  const [dialog, setDialog] = useState<DialogAction | null>(null);
  const [editing, setEditing] = useState(false);
  return (
    <div className="page">
      <QueryView query={q}>
        {(t) => (
          <>
            <Header t={t} onAction={setDialog} onEdit={() => setEditing(true)} />
            <Trace t={t} />
            <Tabs<Tab>
              label="Ticket sections"
              value={tab}
              onChange={(v) => setParams(v === 'overview' ? {} : { tab: v }, { replace: true })}
              tabs={[
                { value: 'overview', label: 'Overview' },
                { value: 'discussion', label: 'Discussion', count: t.commentCount },
                { value: 'implementation', label: 'Implementation', count: t.implementations.length },
                { value: 'approvals', label: 'Approvals', count: t.approvals.length + t.acceptances.length },
                { value: 'delivery', label: 'Releases and delivery', count: t.releases.length },
                { value: 'history', label: 'History' },
              ]}
            />
            <div className="split">
              <div className="stack lg" style={{ minWidth: 0 }}>
                {tab === 'overview' && <Overview t={t} />}
                {tab === 'discussion' && <Discussion t={t} />}
                {tab === 'implementation' && (
                  <Implementation t={t} onStart={() => setDialog('start_manual')} />
                )}
                {tab === 'approvals' && <Approvals t={t} />}
                {tab === 'delivery' && <Delivery t={t} />}
                {tab === 'history' && <History t={t} />}
              </div>
              <Side t={t} />
            </div>
            {dialog && (
              <TicketActionDialog
                projectKey={key!}
                number={n}
                action={dialog}
                onClose={() => setDialog(null)}
              />
            )}
            {editing && <EditScope t={t} onClose={() => setEditing(false)} />}
          </>
        )}
      </QueryView>
    </div>
  );
}

function Header({
  t,
  onAction,
  onEdit,
}: {
  t: TicketDetail;
  onAction: (a: DialogAction) => void;
  onEdit: () => void;
}) {
  const me = useMe();
  const watch = useAction(
    () =>
      t.watching
        ? api.del(`/projects/${t.project.key}/tickets/${t.number}/watch`)
        : api.put(`/projects/${t.project.key}/tickets/${t.number}/watch`),
    { invalidate: [['ticket', t.project.key, t.number]] },
  );
  const archive = useAction(
    () =>
      api.post(`/projects/${t.project.key}/tickets/${t.number}/${t.archived ? 'unarchive' : 'archive'}`, {
        expectedVersion: t.version,
      }),
    { success: t.archived ? 'Unarchived' : 'Archived', invalidate: [['ticket', t.project.key, t.number]] },
  );
  const primary = t.viewer.actions.filter((a) => a === 'accept');
  const others = t.viewer.actions.filter(
    (a) => a !== 'accept' && a !== 'mark_ready' && a !== 'rework_in_progress',
  );
  return (
    <div className="stack sm">
      <div className="row small muted" style={{ gap: 12 }}>
        <span className="mono ink">{t.key}</span>
        <TypeLabel type={t.type} />
        <span>Scope revision {t.scopeRevision}</span>
        <span>
          Reported by <ActorName actor={t.reporter} /> · <Timestamp value={t.createdAt} />
        </span>
      </div>
      <div className="page-header">
        <div className="titles">
          <h1 className="page-title" style={{ fontSize: 32 }}>
            {t.title}
          </h1>
          <div className="row">
            <StatusBadge status={t.status} resolution={t.resolution} />
            <PriorityBadge priority={t.priority} />
            <TicketFlags t={t} />
            {t.labels.map((l) => (
              <span key={l} className="chip">
                {l}
              </span>
            ))}
          </div>
        </div>
        <div className="actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => watch.mutate(undefined)}
            icon={t.watching ? <EyeOff size={14} /> : <Eye size={14} />}
          >
            {t.watching ? 'Unwatch' : 'Watch'}
          </Button>
          {t.viewer.canEditScope && (
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Edit scope
            </Button>
          )}
          {others.map((a) => (
            <Button
              key={a}
              size="sm"
              variant={a === 'reject' ? 'danger-outline' : 'secondary'}
              onClick={() => onAction(a === 'rework_todo' ? 'rework' : a)}
            >
              {a === 'rework_todo'
                ? 'Rework'
                : a === 'release_claim'
                  ? 'Return to Todo'
                  : TRANSITIONS[a].label}
            </Button>
          ))}
          {t.viewer.canStartManual && (
            <Button size="sm" variant="secondary" onClick={() => onAction('start_manual')}>
              Take manual ownership
            </Button>
          )}
          {t.status === 'closed' && me.user.role === 'admin' && (
            <Button size="sm" variant="ghost" onClick={() => archive.mutate(undefined)}>
              {t.archived ? 'Unarchive' : 'Archive'}
            </Button>
          )}
          {primary.map((a) => (
            <Button key={a} size="sm" onClick={() => onAction(a)}>
              Accept current scope
            </Button>
          ))}
        </div>
      </div>
      {t.project.archived && <Alert tone="info">This project is archived. The ticket is read-only.</Alert>}
      {t.status === 'closed' && t.resolution === 'rejected' && t.rejectionReason && (
        <Alert tone="info" title="Rejected">
          {t.rejectionReason}
        </Alert>
      )}
      {t.needsClarification && (
        <Alert tone="warning" title="Needs clarification">
          {t.clarificationReason}
        </Alert>
      )}
      {t.staleOwner && t.owner && (
        <Alert tone="error" title="Stale owner">
          <span>
            {t.owner.name}'s claim expired without a heartbeat. Work writes are fenced until an admin recovers
            it.{' '}
            <Link to={`/projects/${t.project.key}/tickets/${t.number}/implementations/${t.owner.displayId}`}>
              Open {t.owner.displayId}
            </Link>
          </span>
        </Alert>
      )}
      <ErrorAlert error={watch.error ?? archive.error} />
    </div>
  );
}

/** INT-03 traceability: scope → context → acceptance → implementation → release approval → delivery. */
function Trace({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const acc = t.acceptances.find((a) => a.current) ?? t.acceptances[0];
  const impl = t.implementations[0];
  const rel = t.releases[0];
  const del = t.deliveries[0];
  const steps = [
    { icon: BookOpen, label: 'Scope', value: `Revision ${t.scopeRevision}`, to: `${base}?tab=history` },
    {
      icon: ShieldCheck,
      label: 'Acceptance',
      value: acc ? `${acc.displayId}${acc.revokedAt ? ' (revoked)' : ''}` : 'Not accepted',
      to: `${base}?tab=approvals`,
    },
    {
      icon: Hammer,
      label: 'Implementation',
      value: impl ? `${impl.displayId} · ${humanize(impl.state)}` : '—',
      to: impl ? `${base}/implementations/${impl.displayId}` : undefined,
    },
    {
      icon: Rocket,
      label: 'Release',
      value: rel ? `${rel.displayId} · ${humanize(rel.state)}` : '—',
      to: rel ? `/projects/${t.project.key}/releases/${rel.displayId}` : undefined,
    },
    {
      icon: PackageCheck,
      label: 'Delivery',
      value: del
        ? `ATT-${del.number} · ${humanize(del.outcome ?? del.state)}${del.provenance ? ` (${del.provenance})` : ''}`
        : '—',
      to: del
        ? `/projects/${t.project.key}/releases/${del.releaseDisplayId}/attempts/ATT-${del.number}`
        : undefined,
    },
  ];
  return (
    <nav className="trace" aria-label="Request to delivery trace">
      {steps.map((s) => (
        <div key={s.label} className="trace-step">
          <span className="row nowrap small muted" style={{ gap: 6 }}>
            <s.icon size={14} aria-hidden /> {s.label}
          </span>
          {s.to ? (
            <Link to={s.to} className="small">
              {s.value}
            </Link>
          ) : (
            <span className="small ink">{s.value}</span>
          )}
        </div>
      ))}
    </nav>
  );
}

function Overview({ t }: { t: TicketDetail }) {
  return (
    <>
      <Card title="Description">
        <Markdown>{t.description}</Markdown>
        {t.stepsOrOutcome && (
          <>
            <h3 className="title-sm">{t.type === 'issue' ? 'Reproduction steps' : 'Intended outcome'}</h3>
            <Markdown>{t.stepsOrOutcome}</Markdown>
          </>
        )}
      </Card>
      <Card
        title="Acceptance criteria"
        subtitle={
          t.status === 'pending'
            ? 'Proposed. Finalized when an admin accepts this scope.'
            : `Accepted at scope revision ${t.scopeRevision}.`
        }
      >
        {t.acceptanceCriteria.length ? (
          <ol style={{ margin: 0, paddingLeft: 20 }} className="stack sm">
            {t.acceptanceCriteria.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ol>
        ) : (
          <span className="small muted">No criteria yet. They are required before acceptance.</span>
        )}
      </Card>
      <AssistantPanel projectKey={t.project.key} number={t.number} ticketVersion={t.version} />
      <Relationships t={t} />
    </>
  );
}

function Relationships({ t }: { t: TicketDetail }) {
  const admin = useIsAdmin();
  const me = useMe();
  const isReporter = t.reporter.kind === 'human' && t.reporter.id === me.user.id;
  const [form, setForm] = useState<
    null | 'parent' | 'dependency' | 'related' | 'blocker' | 'decision' | 'clarify'
  >(null);
  const [override, setOverride] = useState<{
    dependencyId?: string;
    manualBlockerId?: string;
    label: string;
  } | null>(null);
  const [clear, setClear] = useState<string | null>(null);
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const inv = [
    ['ticket', t.project.key, t.number],
    ['board', t.project.key],
  ];
  const removeDep = useAction(
    (d: { id: string; reason: string }) =>
      api.del(`${base}/dependencies/${d.id}?reason=${encodeURIComponent(d.reason)}`),
    { success: 'Dependency removed', invalidate: inv },
  );
  const removeRelated = useAction((id: string) => api.del(`${base}/related/${id}`), {
    success: 'Link removed',
    invalidate: inv,
  });
  const editable = !t.project.archived;
  const rejectedChildren = t.descendants.filter((d) => d.status === 'closed' && d.resolution === 'rejected');
  const row = (
    k: {
      key: string;
      number: number;
      title: string;
      status: TicketDetail['status'];
      resolution: TicketDetail['resolution'];
    },
    extra?: React.ReactNode,
  ) => (
    <li key={k.key} style={{ justifyContent: 'space-between' }}>
      <span style={{ minWidth: 0 }}>
        <Link to={`/projects/${t.project.key}/tickets/${k.number}`} className="mono">
          {k.key}
        </Link>{' '}
        {k.title}
      </span>
      <span className="row nowrap">
        <StatusBadge status={k.status} resolution={k.resolution} />
        {extra}
      </span>
    </li>
  );
  return (
    <Card
      title="Relationships and blockers"
      subtitle="Structure is part of accepted scope; admins change it. Blocked and clarification are indicators, not columns."
      action={
        editable && (
          <div className="row">
            {admin && (
              <Button size="sm" variant="secondary" onClick={() => setForm('parent')}>
                Set parent
              </Button>
            )}
            {admin && (
              <Button size="sm" variant="secondary" onClick={() => setForm('dependency')}>
                Add blocker link
              </Button>
            )}
            {t.viewer.canComment && (
              <Button size="sm" variant="ghost" onClick={() => setForm('related')}>
                Relate
              </Button>
            )}
            {admin && (
              <Button size="sm" variant="ghost" onClick={() => setForm('blocker')}>
                Record blocker
              </Button>
            )}
            {(admin || isReporter) && (
              <Button size="sm" variant="ghost" onClick={() => setForm('clarify')}>
                {t.needsClarification ? 'Resolve clarification' : 'Needs clarification'}
              </Button>
            )}
          </div>
        )
      }
    >
      <div className="grid-2">
        <div className="stack sm">
          <span className="small strong">Parent</span>
          <ul className="checklist">{t.parent ? row(t.parent) : <li className="small muted">None</li>}</ul>
          <span className="small strong">Children</span>
          <ul className="checklist">
            {t.children.length ? t.children.map((c) => row(c)) : <li className="small muted">None</li>}
          </ul>
          <span className="small strong">Related</span>
          <ul className="checklist">
            {t.related.length ? (
              t.related.map((r) =>
                row(
                  r,
                  editable && t.viewer.canComment && (
                    <Button size="sm" variant="ghost" onClick={() => removeRelated.mutate(r.linkId)}>
                      Unlink
                    </Button>
                  ),
                ),
              )
            ) : (
              <li className="small muted">None</li>
            )}
          </ul>
        </div>
        <div className="stack sm">
          <span className="small strong">Blocked by</span>
          <ul className="checklist">
            {t.prerequisites.length ? (
              t.prerequisites.map((p) =>
                row(
                  p,
                  admin && editable && (
                    <>
                      {!t.blockers.find((b) => b.id === p.dependencyId)?.overridden &&
                        !(p.status === 'closed' && p.resolution === 'done') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setOverride({ dependencyId: p.dependencyId, label: `dependency on ${p.key}` })
                            }
                          >
                            Override
                          </Button>
                        )}
                      <Button size="sm" variant="ghost" onClick={() => setClear(`dep:${p.dependencyId}`)}>
                        Remove
                      </Button>
                    </>
                  ),
                ),
              )
            ) : (
              <li className="small muted">None</li>
            )}
          </ul>
          <span className="small strong">Blocking</span>
          <ul className="checklist">
            {t.dependents.length ? t.dependents.map((d) => row(d)) : <li className="small muted">None</li>}
          </ul>
          <span className="small strong">Manual blockers</span>
          <ul className="checklist">
            {t.manualBlockers.filter((b) => !b.clearedAt).length ? (
              t.manualBlockers
                .filter((b) => !b.clearedAt)
                .map((b) => {
                  const info = t.blockers.find((x) => x.id === b.id);
                  return (
                    <li key={b.id} style={{ justifyContent: 'space-between' }}>
                      <span>
                        {b.reason} {info?.overridden && <Badge tone="info">Overridden</Badge>}
                      </span>
                      {admin && editable && (
                        <span className="row nowrap">
                          {!info?.overridden && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setOverride({ manualBlockerId: b.id, label: 'manual blocker' })}
                            >
                              Override
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setClear(`blk:${b.id}`)}>
                            Clear
                          </Button>
                        </span>
                      )}
                    </li>
                  );
                })
            ) : (
              <li className="small muted">None</li>
            )}
          </ul>
          {t.overrides.filter((o) => o.active).length > 0 && (
            <>
              <span className="small strong">Active overrides (scope revision {t.scopeRevision})</span>
              <ul className="checklist">
                {t.overrides
                  .filter((o) => o.active)
                  .map((o) => (
                    <li key={o.id} className="small">
                      “{o.reason}” — <ActorName actor={o.admin} />, <Timestamp value={o.createdAt} />
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      </div>
      {rejectedChildren.length > 0 && (
        <Alert
          tone={rejectedChildren.every((c) => c.scopeDecisionRecorded) ? 'info' : 'warning'}
          title="Rejected children"
        >
          <ul>
            {rejectedChildren.map((c) => (
              <li key={c.ticketId}>
                {c.key}:{' '}
                {c.scopeDecisionRecorded
                  ? 'parent-scope decision recorded'
                  : 'needs an admin parent-scope decision'}
              </li>
            ))}
          </ul>
          {admin && editable && (
            <Button size="sm" variant="secondary" onClick={() => setForm('decision')}>
              Record parent-scope decision
            </Button>
          )}
          {t.scopeDecisions
            .filter((d) => d.parentTicketId === t.id)
            .map((d) => (
              <span key={d.id} className="small">
                {d.child?.key}: “{d.decision}” — <ActorName actor={d.admin} />
              </span>
            ))}
        </Alert>
      )}
      {form && <RelationshipForm t={t} kind={form} onClose={() => setForm(null)} />}
      {override && (
        <ReasonDialog
          title={`Override ${override.label}?`}
          description={`Applies only to this ${override.label} at scope revision ${t.scopeRevision}. The prerequisite stays visible and is not marked done.`}
          confirm="Record override"
          onClose={() => setOverride(null)}
          run={(reason) => api.post(`${base}/overrides`, { ...override, reason })}
          invalidate={inv}
        />
      )}
      {clear && (
        <ReasonDialog
          title={clear.startsWith('dep:') ? 'Remove blocking dependency?' : 'Clear blocker?'}
          description={
            clear.startsWith('dep:')
              ? 'Dependencies are part of scope; the ticket gets a new scope revision. Accepted tickets must be reconsidered first.'
              : 'The blocker is cleared with your reason.'
          }
          confirm={clear.startsWith('dep:') ? 'Remove dependency' : 'Clear blocker'}
          onClose={() => setClear(null)}
          run={(reason) =>
            clear.startsWith('dep:')
              ? removeDep.mutateAsync({ id: clear.slice(4), reason })
              : api.post(`${base}/blockers/${clear.slice(4)}/clear`, { reason })
          }
          invalidate={inv}
        />
      )}
    </Card>
  );
}

function ReasonDialog({
  title,
  description,
  confirm,
  onClose,
  run,
  invalidate,
  danger,
}: {
  title: string;
  description: string;
  confirm: string;
  onClose: () => void;
  run: (reason: string) => Promise<unknown>;
  invalidate: unknown[][];
  danger?: boolean;
}) {
  const [reason, setReason] = useState('');
  const m = useAction(() => run(reason), {
    success: 'Saved',
    invalidate: invalidate as never,
    onSuccess: onClose,
  });
  return (
    <ConfirmDialog
      title={title}
      confirmLabel={confirm}
      variant={danger ? 'danger' : 'primary'}
      onClose={onClose}
      onConfirm={() => m.mutate(undefined)}
      pending={m.isPending}
      error={m.error}
      disabled={!reason.trim()}
    >
      <p className="muted">{description}</p>
      <Field label="Reason">
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-autofocus
          />
        )}
      </Field>
    </ConfirmDialog>
  );
}

function RelationshipForm({
  t,
  kind,
  onClose,
}: {
  t: TicketDetail;
  kind: 'parent' | 'dependency' | 'related' | 'blocker' | 'decision' | 'clarify';
  onClose: () => void;
}) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const [value, setValue] = useState(kind === 'parent' ? (t.parent?.key ?? '') : '');
  const [text, setText] = useState('');
  const rejected = t.descendants.filter(
    (d) => d.status === 'closed' && d.resolution === 'rejected' && !d.scopeDecisionRecorded,
  );
  const titles = {
    parent: 'Set parent',
    dependency: 'Add blocking dependency',
    related: 'Relate a ticket',
    blocker: 'Record blocker',
    decision: 'Parent-scope decision',
    clarify: t.needsClarification ? 'Resolve clarification' : 'Mark as needing clarification',
  };
  const m = useAction(
    () => {
      switch (kind) {
        case 'parent':
          return api.put(`${base}/parent`, { parentKey: value.trim() || null, expectedVersion: t.version });
        case 'dependency':
          return api.post(`${base}/dependencies`, { targetKey: value, expectedVersion: t.version });
        case 'related':
          return api.post(`${base}/related`, { targetKey: value });
        case 'blocker':
          return api.post(`${base}/blockers`, { reason: text });
        case 'decision':
          return api.post(`${base}/scope-decisions`, {
            childTicketKey: value || rejected[0]?.key,
            decision: text,
          });
        case 'clarify':
          return api.put(`${base}/clarification`, { needed: !t.needsClarification, reason: text });
      }
    },
    {
      success: 'Saved',
      invalidate: [
        ['ticket', t.project.key, t.number],
        ['board', t.project.key],
      ],
      onSuccess: onClose,
    },
  );
  const structural = kind === 'parent' || kind === 'dependency';
  return (
    <Modal
      title={titles[kind]}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending}>
            Save
          </Button>
        </>
      }
    >
      {structural && (
        <Alert tone="info">
          Structure is part of scope. Pending tickets get a new scope revision; accepted tickets must be
          reconsidered first. Cycles are refused.
        </Alert>
      )}
      {(kind === 'parent' || kind === 'dependency' || kind === 'related') && (
        <Field
          label={
            kind === 'parent'
              ? 'Parent ticket'
              : kind === 'dependency'
                ? 'Prerequisite ticket (must be done first)'
                : 'Related ticket'
          }
          hint={
            kind === 'parent'
              ? 'Leave empty to remove the parent.'
              : `Same project, e.g. ${t.project.key}-12.`
          }
        >
          {(p) => (
            <Input
              {...p}
              className="mono"
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              placeholder={`${t.project.key}-12`}
              data-autofocus
            />
          )}
        </Field>
      )}
      {kind === 'decision' && (
        <Field label="Rejected child">
          {(p) => (
            <Select {...p} value={value || rejected[0]?.key} onChange={(e) => setValue(e.target.value)}>
              {rejected.map((r) => (
                <option key={r.ticketId} value={r.key}>
                  {r.key}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      {(kind === 'blocker' || kind === 'decision' || kind === 'clarify') && (
        <Field
          label={
            kind === 'decision'
              ? 'Why the parent scope remains valid (or what changes)'
              : kind === 'clarify'
                ? t.needsClarification
                  ? 'Resolution note'
                  : 'What needs clarification'
                : 'Reason'
          }
        >
          {(p) => (
            <Textarea {...p} rows={3} value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
          )}
        </Field>
      )}
      {kind === 'decision' && (
        <p className="small muted">
          If the parent's intent changes, also reconsider and re-accept the parent.
        </p>
      )}
      <ErrorAlert error={m.error} />
    </Modal>
  );
}

interface CommentRow {
  id: string;
  author: { kind: 'human' | 'ai_service' | 'system'; name: string };
  body: string | null;
  createdAt: string;
  editedAt: string | null;
  deleted: { at: string; by: { name: string } | null; reason: string | null } | null;
  version: number;
  revisions: { revision: number; body: string; createdAt: string }[] | number;
  attachments: { id: string; displayId: string; filename: string; scanState: string; sizeBytes: number }[];
  canEdit: boolean;
  canDelete: boolean;
}

function Discussion({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const q = useInfiniteQuery({
    queryKey: ['comments', t.project.key, t.number],
    queryFn: ({ pageParam }) =>
      api.get<{ items: CommentRow[]; nextCursor: string | null }>(
        `${base}/comments${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  return (
    <>
      <Card
        title="Discussion"
        subtitle="Proposals in comments are not approved scope. Mentions notify people with access."
      >
        <QueryView query={q}>
          {(d) => {
            const items = d.pages.flatMap((p) => p.items);
            return (
              <div>
                {items.length === 0 && <span className="small muted">No comments yet.</span>}
                {items.map((c) => (
                  <CommentItem key={c.id} c={c} t={t} />
                ))}
                <LoadMore
                  hasMore={!!q.hasNextPage}
                  onClick={() => q.fetchNextPage()}
                  loading={q.isFetchingNextPage}
                />
              </div>
            );
          }}
        </QueryView>
        {t.viewer.canComment && <Composer t={t} />}
      </Card>
      <Files t={t} />
    </>
  );
}

function CommentItem({ c, t }: { c: CommentRow; t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const isAdmin = useIsAdmin();
  const me = useMe();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(c.body ?? '');
  const [deleting, setDeleting] = useState(false);
  const inv = [['comments', t.project.key, t.number]];
  const save = useAction(() => api.patch(`${base}/comments/${c.id}`, { body, expectedVersion: c.version }), {
    success: 'Comment updated',
    invalidate: inv,
    onSuccess: () => setEditing(false),
  });
  const moderating = c.canDelete && !c.canEdit && isAdmin;
  return (
    <div className="comment" id={`comment-${c.id}`}>
      <Avatar name={c.author.name} kind={c.author.kind} />
      <div className="stack sm" style={{ minWidth: 0 }}>
        <div className="row small">
          <span className="strong">{c.author.name}</span>
          {c.author.kind === 'ai_service' && (
            <Badge tone="info" dot={false}>
              AI
            </Badge>
          )}
          <span className="muted">
            <Timestamp value={c.createdAt} />
            {c.editedAt && ' · edited'}
          </span>
          {Array.isArray(c.revisions) && c.revisions.length > 1 && (
            <details>
              <summary className="small muted" style={{ cursor: 'pointer' }}>
                {c.revisions.length} revisions
              </summary>
              <ol className="small">
                {c.revisions.map((r) => (
                  <li key={r.revision}>
                    <Timestamp value={r.createdAt} />: {r.body.slice(0, 200)}
                  </li>
                ))}
              </ol>
            </details>
          )}
        </div>
        {c.deleted ? (
          <span className="small muted" style={{ fontStyle: 'italic' }}>
            Comment removed by {c.deleted.by?.name ?? 'its author'}
            {c.deleted.reason ? ` — ${c.deleted.reason}` : ''}
          </span>
        ) : editing ? (
          <>
            <Textarea
              aria-label="Edit comment"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <ErrorAlert error={save.error} />
            <div className="row">
              <Button size="sm" onClick={() => save.mutate(undefined)} loading={save.isPending}>
                Save
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <Markdown>{c.body ?? ''}</Markdown>
        )}
        {c.attachments.length > 0 && (
          <div className="row">
            {c.attachments.map((a) => (
              <FileChip key={a.id} t={t} file={a} />
            ))}
          </div>
        )}
        {!c.deleted && !t.project.archived && (c.canEdit || c.canDelete) && (
          <div className="row">
            {c.canEdit && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {c.canDelete && (
              <Button size="sm" variant="ghost" onClick={() => setDeleting(true)}>
                {moderating ? 'Moderate' : 'Delete'}
              </Button>
            )}
          </div>
        )}
      </div>
      {deleting &&
        (moderating ? (
          <ReasonDialog
            title="Remove this comment?"
            description="A tombstone with your name and reason remains; edit history is kept for authorized reviewers."
            confirm="Remove comment"
            danger
            onClose={() => setDeleting(false)}
            run={(reason) => api.del(`${base}/comments/${c.id}?reason=${encodeURIComponent(reason)}`)}
            invalidate={inv}
          />
        ) : (
          <DeleteOwn
            onClose={() => setDeleting(false)}
            run={() => api.del(`${base}/comments/${c.id}`)}
            inv={inv}
            me={me.user.displayName}
          />
        ))}
    </div>
  );
}

function DeleteOwn({
  onClose,
  run,
  inv,
}: {
  onClose: () => void;
  run: () => Promise<unknown>;
  inv: unknown[][];
  me: string;
}) {
  const m = useAction(run, { success: 'Comment removed', invalidate: inv as never, onSuccess: onClose });
  return (
    <ConfirmDialog
      title="Delete your comment?"
      confirmLabel="Yes, delete"
      variant="danger"
      onClose={onClose}
      onConfirm={() => m.mutate(undefined)}
      pending={m.isPending}
      error={m.error}
    >
      <p>A tombstone remains in the discussion.</p>
    </ConfirmDialog>
  );
}

function Composer({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const people = useQuery({
    queryKey: ['people', t.project.key],
    queryFn: () => api.get<{ id: string; displayName: string }[]>(`/projects/${t.project.key}/people`),
  });
  const post = useAction(
    async () => {
      const c = await api.post<{ id: string }>(`${base}/comments`, { body });
      if (file) {
        const form = new FormData();
        form.append('commentId', c.id);
        form.append('file', file);
        await api.upload(`${base}/attachments`, form);
      }
      return c;
    },
    {
      success: 'Comment posted',
      invalidate: [
        ['comments', t.project.key, t.number],
        ['ticket', t.project.key, t.number],
      ],
      onSuccess: () => {
        setBody('');
        setFile(null);
      },
    },
  );
  const mention = (id: string) => {
    const p = people.data?.find((x) => x.id === id);
    if (!p) return;
    setBody((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}@[${p.displayName}](user:${p.id}) `);
    ref.current?.focus();
  };
  return (
    <form
      className="stack sm"
      style={{ borderTop: '1px solid var(--color-hairline)', paddingTop: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        post.mutate(undefined);
      }}
    >
      <Field label="Add a comment" hint="Markdown supported.">
        {(p) => <Textarea {...p} ref={ref} rows={4} value={body} onChange={(e) => setBody(e.target.value)} />}
      </Field>
      <div className="row">
        <Select
          aria-label="Mention someone"
          value=""
          onChange={(e) => mention(e.target.value)}
          style={{ width: 200, height: 32, fontSize: 13 }}
        >
          <option value="">Mention someone…</option>
          {(people.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </Select>
        <label className="btn btn-secondary sm" style={{ cursor: 'pointer' }}>
          <Paperclip size={14} aria-hidden /> {file ? file.name : 'Attach file'}
          <input
            type="file"
            className="visually-hidden"
            accept="image/*,.txt,.log,.csv,.md,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <span style={{ flex: 1 }} />
        <Button type="submit" loading={post.isPending} disabled={!body.trim()}>
          Comment
        </Button>
      </div>
      <ErrorAlert error={post.error} />
    </form>
  );
}

function FileChip({
  t,
  file,
}: {
  t: TicketDetail;
  file: { displayId: string; filename: string; scanState: string; sizeBytes: number };
}) {
  const href = `/api/v1/projects/${t.project.key}/tickets/${t.number}/attachments/${file.displayId}/download`;
  return (
    <span className="chip" style={{ height: 'auto', padding: '4px 10px', gap: 8 }}>
      <Paperclip size={12} aria-hidden />
      {file.scanState === 'clean' ? (
        <a href={href} download>
          {file.filename}
        </a>
      ) : (
        <span>{file.filename}</span>
      )}
      <span className="muted">{formatBytes(file.sizeBytes)}</span>
      {file.scanState !== 'clean' && (
        <StateBadge
          state={file.scanState}
          label={file.scanState === 'pending' ? 'Scanning' : humanize(file.scanState)}
        />
      )}
    </span>
  );
}

function Files({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const admin = useIsAdmin();
  const me = useMe();
  const [removing, setRemoving] = useState<TicketDetail['attachments'][number] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const upload = useAction(
    () => {
      const form = new FormData();
      form.append('file', file!);
      return api.upload(`${base}/attachments`, form);
    },
    {
      success: 'Uploaded; scanning before download',
      invalidate: [['ticket', t.project.key, t.number]],
      onSuccess: () => setFile(null),
    },
  );
  return (
    <Card title="Files" subtitle="Files are scanned before download. Removed files keep their metadata.">
      {t.attachments.length === 0 ? (
        <span className="small muted">No files.</span>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>File</th>
                <th>Uploaded by</th>
                <th>State</th>
                <th className="num">Size</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {t.attachments.map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.scanState === 'clean' ? (
                      <a
                        href={`/api/v1${base}/attachments/${a.displayId}/download`}
                        download
                        className="row nowrap"
                        style={{ gap: 6 }}
                      >
                        <Download size={13} aria-hidden /> {a.filename}
                      </a>
                    ) : (
                      <span className={a.removedAt ? 'muted' : ''}>{a.filename}</span>
                    )}
                    <div className="mono small muted">
                      {a.displayId}
                      {a.removeReason ? ` · removed: ${a.removeReason}` : ''}
                    </div>
                  </td>
                  <td className="small">
                    <ActorName actor={a.uploader} /> · <Timestamp value={a.createdAt} dateOnly />
                  </td>
                  <td>
                    <StateBadge
                      state={a.scanState}
                      label={a.scanState === 'pending' ? 'Scanning' : undefined}
                    />
                  </td>
                  <td className="num">{formatBytes(a.sizeBytes)}</td>
                  <td>
                    {a.canRemove && !t.project.archived && (
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(a)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {t.viewer.canComment && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            upload.mutate(undefined);
          }}
        >
          <input
            type="file"
            aria-label="Choose file"
            accept="image/*,.txt,.log,.csv,.md,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button size="sm" type="submit" disabled={!file} loading={upload.isPending}>
            Upload
          </Button>
        </form>
      )}
      <ErrorAlert error={upload.error} />
      {removing &&
        (admin && removing.uploader.id !== me.user.id ? (
          <ReasonDialog
            title={`Remove ${removing.filename}?`}
            description="Content stops being downloadable; metadata and attribution remain. Files bound to evidence require rework first."
            confirm="Remove file"
            danger
            onClose={() => setRemoving(null)}
            run={(reason) =>
              api.del(`${base}/attachments/${removing.displayId}?reason=${encodeURIComponent(reason)}`)
            }
            invalidate={[['ticket', t.project.key, t.number]]}
          />
        ) : (
          <DeleteOwn
            onClose={() => setRemoving(null)}
            run={() => api.del(`${base}/attachments/${removing.displayId}`)}
            inv={[['ticket', t.project.key, t.number]]}
            me=""
          />
        ))}
    </Card>
  );
}

function Implementation({ t, onStart }: { t: TicketDetail; onStart: () => void }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  return (
    <>
      <Card
        title="Implementation attempts"
        subtitle="Each claim is a new generation. Work references carry over to the next attempt."
        action={
          t.viewer.canStartManual && (
            <Button size="sm" variant="secondary" onClick={onStart}>
              Take manual ownership
            </Button>
          )
        }
      >
        {t.implementations.length === 0 ? (
          <span className="small muted">
            {t.status === 'todo'
              ? t.blocked
                ? 'Blocked: resolve blockers before work can start.'
                : t.assignee
                  ? `Assigned to ${t.assignee.name}; not AI-claimable.`
                  : 'Eligible for an AI claim.'
              : 'No implementation yet.'}
          </span>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Attempt</th>
                  <th>Owner</th>
                  <th>State</th>
                  <th>Detail</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {t.implementations.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Link to={`${base}/implementations/${i.displayId}`} className="mono">
                        {i.displayId}
                      </Link>
                    </td>
                    <td>
                      <ActorName actor={i.owner} />
                    </td>
                    <td>
                      <StateBadge state={i.state} label={i.state === 'stale' ? 'Stale owner' : undefined} />
                    </td>
                    <td className="small">
                      gen {i.generation} · scope rev {i.scopeRevision}
                      {i.branch ? ` · ${i.branch}` : ''}
                      {i.terminationReason ? ` · ${i.terminationReason}` : ''}
                    </td>
                    <td className="small">
                      <Timestamp value={i.startedAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Card
        title="Readiness snapshots"
        subtitle="Frozen when work enters Pending release. Readiness does not authorize deployment."
      >
        {t.readiness.length === 0 ? (
          <span className="small muted">None yet.</span>
        ) : (
          <ul className="checklist">
            {t.readiness.map((r) => (
              <li key={r.id} className="small">
                <span className="mono">{r.sourceRevision.slice(0, 12)}</span> · scope rev {r.scopeRevision} ·{' '}
                <Timestamp value={r.createdAt} />
                {r.invalidatedAt ? (
                  <Badge>Invalidated: {r.invalidatedReason}</Badge>
                ) : (
                  <Badge tone="success">Current</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function Approvals({ t }: { t: TicketDetail }) {
  return (
    <>
      <Card title="Acceptance decisions" subtitle="Authenticated admin decisions for exact scope revisions.">
        {t.acceptances.length === 0 ? (
          <span className="small muted">Not accepted yet.</span>
        ) : (
          <ul className="checklist">
            {t.acceptances.map((a) => (
              <li key={a.id} className="small" style={{ justifyContent: 'space-between' }}>
                <span>
                  <span className="mono">{a.displayId}</span> · revision {a.scopeRevision} · accepted by{' '}
                  <ActorName actor={a.admin} /> (
                  {a.requestOrigin === 'mcp' ? 'requested via MCP' : 'directly in the app'}) ·{' '}
                  <Timestamp value={a.createdAt} />
                </span>
                {a.revokedAt ? (
                  <Badge>Revoked: {a.revokedReason}</Badge>
                ) : a.current ? (
                  <Badge tone="success">Current</Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Approval requests">
        {t.approvals.length === 0 ? (
          <span className="small muted">No requests.</span>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Intent</th>
                  <th>Requester</th>
                  <th>State</th>
                  <th>Decided by</th>
                </tr>
              </thead>
              <tbody>
                {t.approvals.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/approvals/${a.displayId}`} className="mono">
                        {a.displayId}
                      </Link>
                    </td>
                    <td>
                      {humanize(a.intent)} · rev {a.targetRevision}
                    </td>
                    <td>
                      <ActorName actor={a.requester} />
                    </td>
                    <td>
                      <StateBadge state={a.state} />
                    </td>
                    <td>
                      <ActorName actor={a.decidedBy} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function Delivery({ t }: { t: TicketDetail }) {
  return (
    <Card
      title="Releases and delivery"
      subtitle="Reported results come from external executors. Verified results add an admin decision."
    >
      {t.releases.length === 0 ? (
        <span className="small muted">Not included in a release.</span>
      ) : (
        <ul className="checklist">
          {t.releases.map((r) => (
            <li key={r.id} style={{ justifyContent: 'space-between' }}>
              <Link to={`/projects/${t.project.key}/releases/${r.displayId}`}>
                <span className="mono">{r.displayId}</span> {r.title}
              </Link>
              <span className="row nowrap">
                <StateBadge state={r.state} />
                {r.reserved && (
                  <Badge tone="info" dot={false}>
                    Reserved
                  </Badge>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {t.deliveries.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Attempt</th>
                <th>Kind</th>
                <th>State</th>
                <th>Provenance</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              {t.deliveries.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link
                      to={`/projects/${t.project.key}/releases/${d.releaseDisplayId}/attempts/ATT-${d.number}`}
                      className="mono"
                    >
                      {d.releaseDisplayId} · ATT-{d.number}
                    </Link>
                  </td>
                  <td>{humanize(d.kind)}</td>
                  <td>
                    <StateBadge state={d.state} />
                  </td>
                  <td>{d.provenance ? <StateBadge state={d.provenance} /> : '—'}</td>
                  <td className="small">
                    <Timestamp value={d.registeredAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function History({ t }: { t: TicketDetail }) {
  const q = useInfiniteQuery({
    queryKey: ['ticket-activity', t.project.key, t.number],
    queryFn: ({ pageParam }) =>
      api.get<Page<AuditEvent>>(
        `/projects/${t.project.key}/tickets/${t.number}/activity${pageParam ? `?cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  return (
    <>
      <Card title="Scope revisions">
        {t.revisions.map((r) => (
          <details key={r.revision} open={r.revision === t.scopeRevision}>
            <summary style={{ cursor: 'pointer' }}>
              <span className="strong">Revision {r.revision}</span>{' '}
              <span className="small muted">
                · <ActorName actor={r.author} /> · <Timestamp value={r.createdAt} />
                {r.reason ? ` · ${r.reason}` : ''}
              </span>
            </summary>
            <div className="well stack sm" style={{ marginTop: 8 }}>
              <span className="strong">{r.title}</span>
              <Markdown>{r.description}</Markdown>
              {r.acceptanceCriteria.length > 0 && (
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  {r.acceptanceCriteria.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ol>
              )}
            </div>
          </details>
        ))}
      </Card>
      <Card title="Activity">
        <QueryView query={q}>
          {(d) => (
            <>
              <EventTable events={d.pages.flatMap((p) => p.items)} />
              <LoadMore
                hasMore={!!q.hasNextPage}
                onClick={() => q.fetchNextPage()}
                loading={q.isFetchingNextPage}
              />
            </>
          )}
        </QueryView>
      </Card>
    </>
  );
}

function Side({ t }: { t: TicketDetail }) {
  const admin = useIsAdmin();
  return (
    <div className="stack">
      <Card title="Details" tight>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={t.status} resolution={t.resolution} />],
            ['Priority', <PriorityBadge key="p" priority={t.priority} />],
            ['Reporter', <ActorName key="r" actor={t.reporter} />],
            [
              'Assignee',
              t.assignee ? (
                <ActorName actor={t.assignee} />
              ) : (
                <span className="muted">
                  {t.status === 'todo' ? 'Unassigned · AI-claimable' : 'Unassigned'}
                </span>
              ),
            ],
            [
              'Owner',
              t.owner ? (
                <span>
                  {t.owner.name}
                  {t.owner.kind === 'ai_service' ? ' (AI)' : ''}
                  {t.owner.state === 'stale' ? ' · stale' : ''}
                </span>
              ) : (
                '—'
              ),
            ],
            ['Labels', t.labels.length ? t.labels.join(', ') : '—'],
            ['Created', <Timestamp key="c" value={t.createdAt} />],
            ['Updated', <Timestamp key="u" value={t.updatedAt} />],
            ['In column since', <Timestamp key="sc" value={t.statusChangedAt} />],
            ...(t.closedAt
              ? ([['Closed', <Timestamp key="cl" value={t.closedAt} />]] as [string, React.ReactNode][])
              : []),
          ]}
        />
      </Card>
      {admin && !t.project.archived && t.status !== 'closed' && <Triage t={t} />}
      <Card title="Watchers" tight>
        <span className="small">
          {t.watchers
            .filter(Boolean)
            .map((w) => w!.name)
            .join(', ') || 'Nobody'}
        </span>
      </Card>
    </div>
  );
}

function Triage({ t }: { t: TicketDetail }) {
  const people = useQuery({
    queryKey: ['people', t.project.key],
    queryFn: () => api.get<{ id: string; displayName: string }[]>(`/projects/${t.project.key}/people`),
  });
  const [priority, setPriority] = useState<Priority>(t.priority);
  const [assignee, setAssignee] = useState(t.assignee?.id ?? '');
  const [labels, setLabels] = useState(t.labels.join(', '));
  useEffect(() => {
    setPriority(t.priority);
    setAssignee(t.assignee?.id ?? '');
    setLabels(t.labels.join(', '));
  }, [t.priority, t.assignee?.id, t.labels]);
  const save = useAction(
    () =>
      api.patch(`/projects/${t.project.key}/tickets/${t.number}/triage`, {
        priority,
        assigneeUserId: assignee || null,
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
        expectedVersion: t.version,
      }),
    {
      success: 'Triage saved',
      invalidate: [
        ['ticket', t.project.key, t.number],
        ['board', t.project.key],
      ],
    },
  );
  return (
    <Card title="Triage" subtitle="Doesn't change scope or acceptance." tight>
      <Field label="Priority">
        {(p) => (
          <Select {...p} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Assignee" hint="Unassigned todo work is AI-claimable.">
        {(p) => (
          <Select {...p} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {(people.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Labels">
        {(p) => <Input {...p} value={labels} onChange={(e) => setLabels(e.target.value)} />}
      </Field>
      <ErrorAlert error={save.error} />
      <Button size="sm" onClick={() => save.mutate(undefined)} loading={save.isPending}>
        Save triage
      </Button>
    </Card>
  );
}

function EditScope({ t, onClose }: { t: TicketDetail; onClose: () => void }) {
  const [type, setType] = useState(t.type);
  const [title, setTitle] = useState(t.title);
  const [description, setDescription] = useState(t.description);
  const [steps, setSteps] = useState(t.stepsOrOutcome);
  const [criteria, setCriteria] = useState(t.acceptanceCriteria.length ? t.acceptanceCriteria : ['']);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const accepted = t.status !== 'pending';
  const save = useAction(
    () =>
      api.patch(`/projects/${t.project.key}/tickets/${t.number}/scope`, {
        type,
        title,
        description,
        stepsOrOutcome: steps,
        acceptanceCriteria: criteria.filter((c) => c.trim()),
        expectedVersion: t.version,
        confirmReturnToPending: confirm,
        reason: reason || undefined,
      }),
    {
      success: 'Scope saved as a new revision',
      invalidate: [
        ['ticket', t.project.key, t.number],
        ['board', t.project.key],
      ],
      onSuccess: onClose,
    },
  );
  const conflict = save.error instanceof ApiError && save.error.code === 'version_conflict';
  return (
    <Modal
      title={`Edit scope · ${t.key}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate(undefined)}
            loading={save.isPending}
            disabled={accepted && (!confirm || !reason.trim())}
          >
            {accepted ? 'Return to Pending and save' : 'Save new revision'}
          </Button>
        </>
      }
    >
      {accepted ? (
        <Alert tone="warning" title="This scope is accepted">
          <span>
            Saving a material change returns {t.key} to Pending, revokes acceptance and claims, and
            invalidates readiness and release approvals. Prior evidence stays in history.
          </span>
        </Alert>
      ) : (
        <p className="muted small">
          Saving creates scope revision {t.scopeRevision + 1}. Outstanding acceptance requests for revision{' '}
          {t.scopeRevision} become stale.
        </p>
      )}
      <Segmented
        label="Type"
        value={type}
        onChange={setType}
        options={[
          { value: 'issue', label: 'Issue' },
          { value: 'feature_request', label: 'Feature request' },
        ]}
      />
      <Field label="Title">
        {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}
      </Field>
      <Field label="Description">
        {(p) => (
          <Textarea {...p} rows={6} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <Field label={type === 'issue' ? 'Reproduction steps' : 'Intended outcome'} optional>
        {(p) => <Textarea {...p} rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} />}
      </Field>
      <div className="field">
        <span className="label">Acceptance criteria</span>
        <ListEditor label="Criterion" items={criteria} onChange={setCriteria} addLabel="Add criterion" />
      </div>
      {accepted && (
        <>
          <Field label="Reason">
            {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <Checkbox
            label="I understand this returns the ticket to Pending"
            checked={confirm}
            onChange={setConfirm}
          />
        </>
      )}
      {conflict && (
        <Alert tone="warning">
          Someone changed this ticket while you were editing. Your text is kept here; reload the page in
          another tab to compare, then save again.
        </Alert>
      )}
      <ErrorAlert error={conflict ? null : save.error} />
    </Modal>
  );
}
